import React from 'react'

import { BirthdayQuote } from '@facades/birthday'

import {
  OrderAction,
  OrderButton,
  ShareAction,
  ShareButton,
  ShareMessage,
  StickySummaryContainer,
  SummaryActions,
  SummaryContainer,
} from './birthdaySummary.styled'

const money = (value: number) =>
  `${new Intl.NumberFormat('cs-CZ').format(value)} Kč`

export type BirthdayShareControls = {
  canShare: boolean
  isSaving: boolean
  shareUrl?: string
  copied?: boolean
  message?: string
  error?: string
  onSave: () => void
  onCopy: () => void
}

export type BirthdayOrderControls = {
  canOrder: boolean
  isOrdered?: boolean
  onOpen: () => void
}

const SummaryContent = ({
  quote,
  estimatedOnsite,
  share,
  order,
}: {
  quote: BirthdayQuote | undefined
  estimatedOnsite: number
  share?: BirthdayShareControls
  order?: BirthdayOrderControls
}) => {
  const prepayment = quote?.prepayment ?? 0
  const onsite = quote?.onsite ?? estimatedOnsite
  const total = quote?.fixedTotal ?? prepayment + onsite

  return (
    <>
      <div>
        <strong>Předběžná kalkulace</strong>
        <span>Hry a služby předem, občerstvení na místě.</span>
      </div>
      <div>
        <span>Předem</span>
        <strong>{money(prepayment)}</strong>
      </div>
      <div>
        <span>Na místě</span>
        <strong>{money(onsite)}</strong>
      </div>
      <div>
        <span>Celkem</span>
        <strong>{money(total)}</strong>
      </div>
      {(share || order) && (
        <SummaryActions data-summary-actions>
          {order && (
            <OrderAction data-order-action>
              <OrderButton
                data-order-button
                type="button"
                disabled={order.isOrdered}
                onClick={order.onOpen}
              >
                {order.isOrdered ? 'Objednáno' : 'Objednat'}
              </OrderButton>
            </OrderAction>
          )}
          {share && (
            <ShareAction data-share-action>
              <ShareButton
                data-share-button
                type="button"
                disabled={
                  !share.shareUrl && (!share.canShare || share.isSaving)
                }
                title={share.shareUrl}
                onClick={share.shareUrl ? share.onCopy : share.onSave}
              >
                {share.isSaving
                  ? 'Ukládáme…'
                  : share.shareUrl
                  ? share.copied
                    ? 'Odkaz zkopírován'
                    : 'Kopírovat odkaz'
                  : 'Uložit a sdílet'}
              </ShareButton>
              {(share.error || share.message) && (
                <ShareMessage data-share-message $error={Boolean(share.error)}>
                  {share.error ?? share.message}
                </ShareMessage>
              )}
            </ShareAction>
          )}
        </SummaryActions>
      )}
    </>
  )
}

export const BirthdaySummary = ({
  quote,
  estimatedOnsite = 0,
  share,
  order,
}: {
  quote: BirthdayQuote | undefined
  estimatedOnsite?: number
  share?: BirthdayShareControls
  order?: BirthdayOrderControls
}) => {
  return (
    <>
      <SummaryContainer data-testid="summary-original">
        <SummaryContent
          quote={quote}
          estimatedOnsite={estimatedOnsite}
          share={share}
          order={order}
        />
      </SummaryContainer>
      <StickySummaryContainer data-testid="summary-sticky">
        <SummaryContent
          quote={quote}
          estimatedOnsite={estimatedOnsite}
          share={share}
          order={order}
        />
      </StickySummaryContainer>
    </>
  )
}
